import HelpCenterCategoryAndArticlesListWrapper from 'components/HelpCenter/HelpCenterCategoryAndArticlesListWrapper';
import SeoWrapper from 'components/Seo/SeoWrapper';
import { Locale } from 'enums/locale';
import { useBreadcrumbs } from 'hooks/useBreadcrumbs';
import api from 'lib/apiCache';
import { getI18nFileAsync } from 'lib/getI18nFileAsync';
import { getMetadataForCustomPages } from 'lib/seo/seoData';
import { GetStaticPropsContext, NextPage } from 'next';
import { helpCenterService } from 'services/HelpCenterService';
import {
  localizationService,
  SlugTranslations,
} from 'services/LocalizationService';
import { HelpCenterCategory } from 'types/helpCenter.type';
import { HelmetSettings } from 'types/seo.type';

const slugTranslationsPath = 'help-center-categories-slugs';

export async function getStaticPaths() {
  const response = await helpCenterService.getHelpCenterAll();
  const paths = helpCenterService.mapHelpCenterAllToPath(response);
  const pathsForCache = helpCenterService.mapHelpCenterAllToCache(response);

  api.cache.set(pathsForCache, slugTranslationsPath);

  return {
    paths,
    fallback: false,
  };
}

export async function getStaticProps(ctx: GetStaticPropsContext) {
  const slug = ctx.params?.userTypeSlug as string;

  const helpCenterUserType =
    await helpCenterService.getHelpCenterCategoriesByUserType(
      slug,
      ctx.locale as Locale
    );

  const slugs = api.cache.get(
    ctx.params!.userTypeSlug as string,
    slugTranslationsPath
  );

  const helpCenterData = await getI18nFileAsync(
    'helpCenter',
    ctx.locale as Locale
  );

  const userTypeObj = Object.values(helpCenterData.category).find(
    (userType: any) => userType.url === slug
  ) as any;

  const title = userTypeObj.title;
  const description = userTypeObj.description;

  const localeUrl =
    ctx.locale === Locale.EN ? 'en/help-center' : 'yardim-merkezi';
  const url = `https://www.teknevia.com/${localeUrl}/${slug}/`;

  const helmetSettings = getMetadataForCustomPages(
    title,
    url,
    ctx.locale as Locale,
    description
  );

  const slugTranslations = {
    ...(slugs ? { userTypeSlug: slugs } : {}),
  };

  localizationService.slugTranslations = slugTranslations;

  return {
    props: {
      helpCenterUserType,
      slugTranslations,
      helmetSettings,
    },
    revalidate: 10,
  };
}

interface HelpCenterArticlesByUserTypeProps {
  helpCenterUserType: HelpCenterCategory;
  slugTranslations: SlugTranslations;
  helmetSettings: HelmetSettings;
}

const HelpCenterArticlesByUserType: NextPage<
  HelpCenterArticlesByUserTypeProps
> = ({ helpCenterUserType, slugTranslations, helmetSettings }) => {
  const { onlyTekneViaBreadcrumb } = useBreadcrumbs();

  localizationService.slugTranslations = { ...slugTranslations };

  const breadcrumbs = [
    onlyTekneViaBreadcrumb,
    {
      label: 'helpCenter:general.title',
      link: '/yardim-merkezi',
      isTranslatable: true,
    },
    {
      label: helpCenterUserType.title,
      link: `/yardim-merkezi/${helpCenterUserType.slug}`,
      isTranslatable: false,
    },
  ];

  return (
    <SeoWrapper helmetSettings={helmetSettings}>
      <HelpCenterCategoryAndArticlesListWrapper
        helpCenterCategories={helpCenterUserType.help_center_categories}
        breadcrumbItems={breadcrumbs}
        title={helpCenterUserType.title}
        description={helpCenterUserType.description}
        isVisibleArticleTitle={true}
      />
    </SeoWrapper>
  );
};

export default HelpCenterArticlesByUserType;
